/* import __COLOCATED_TEMPLATE__ from './trending-topic-card.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import type { TrendingTopic } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topic-trends/trending-topics-data-resources';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { VISUALIZATION_TYPES } from 'embercom/components/reporting/custom/chart-builder/filter-bar/chart-type-filter';
import { insightsMetrics } from 'embercom/components/reporting/automation/ai-insights/fin-insights/jobs';
import moment from 'moment-timezone';
import Range from 'embercom/models/reporting/range';
import type { LogicalFilter } from 'embercom/components/reporting/custom/filters';
import { metricConfigs } from 'embercom/components/reporting/automation/fin-ai-agent/analyze/topics-explorer/common';
import PALETTE from '@intercom/pulse/lib/palette';
import { cached } from 'tracked-toolbox';

const TREND_CHART_HEIGHT = 88;
const TREND_CHART_WIDTH = 208;

interface Args {
  trendingTopic: TrendingTopic;
  onShowConversationExplorer: (topic: TrendingTopic) => void;
  trackAnalyticsEvent: (action: string, object: string, extraContext: Record<string, any>) => void;
  selectedWeekStart?: string;
}

export default class TrendingTopicCard extends Component<Args> {
  @service declare intl: IntlService;

  get topicName() {
    return this.args.trendingTopic.topicName;
  }

  get subtopicName() {
    return this.args.trendingTopic.subtopicName;
  }

  get statsDateRange() {
    // We don't use the app's timezone here, the date range is determined by an ML-pipeline, which uses UTC time
    let weekStartMoment = moment.utc(this.args.trendingTopic.weekStart);
    let weekEndMoment = weekStartMoment.clone().add(6, 'days');
    return Range.createFromParams(weekStartMoment.format(), weekEndMoment.format(), 'UTC');
  }

  get statsFilters(): LogicalFilter {
    return {
      type: 'and',
      filters: [
        {
          type: 'category',
          data: {
            attribute: 'conversation_custom_fields#ai_insights_topic',
            property: 'conversation_custom_fields#ai_insights_topic',
            values: [this.topicName],
          },
        },
        {
          type: 'category',
          data: {
            attribute: 'conversation_custom_fields#ai_insights_subtopic',
            property: 'conversation_custom_fields#ai_insights_subtopic',
            values: [this.subtopicName],
          },
        },
      ],
    };
  }

  get primaryMetricGraphLabel() {
    let translationFor = (name: string) => {
      return this.intl.t(
        `components.reporting.automation.fin-insights.analyse.topic-trends.graph-labels.${name}`,
      );
    };
    return translationFor(this.args.trendingTopic.primaryMetric);
  }

  @cached
  get metricConfigs() {
    return metricConfigs(this.intl);
  }

  get primaryMetricConfig() {
    let primaryMetric = this.args.trendingTopic.primaryMetric;
    if (primaryMetric === 'v1.new_conversations') {
      primaryMetric = Object.keys(this.metricConfigs)[0];
    }
    return this.metricConfigs[primaryMetric];
  }

  get trendChartDefinition() {
    let dateRangeConfig;
    if (this.args.selectedWeekStart) {
      // Calculate 6 complete weeks including the selected week
      let selectedWeekMoment = moment.utc(this.args.selectedWeekStart);
      let startDate = selectedWeekMoment.clone().subtract(5, 'weeks').add(1, 'day');
      let endDate = selectedWeekMoment.clone().endOf('isoWeek');

      dateRangeConfig = {
        selection: 'custom',
        start: startDate.format('YYYY-MM-DD'),
        end: endDate.format('YYYY-MM-DD'),
      };
    } else {
      dateRangeConfig = {
        selection: 'past_4_weeks',
      };
    }

    let volumeMetricId = insightsMetrics(this.intl).volume.metric_id;
    let isPrimaryMetricVolume = this.args.trendingTopic.primaryMetric === volumeMetricId;

    let chartSeries: Array<{
      metric_id: string;
      filters: LogicalFilter;
      color?: string;
      label: string;
      aggregation?: string;
    }> = [
      {
        metric_id: volumeMetricId,
        filters: this.statsFilters,
        color: PALETTE['vis-azure-50'],
        label: this.intl.t('ai-agent.analyze.performance.new-conversations'),
      },
    ];

    if (!isPrimaryMetricVolume) {
      chartSeries.push({
        ...this.primaryMetricConfig.seriesConfig,
        filters: this.statsFilters,
        color: this.primaryMetricConfig.seriesConfig.color,
        label: this.primaryMetricConfig.label,
      });
    }

    return {
      id: `trend-${this.topicName}-${this.subtopicName}`,
      view_by: 'time',
      visualization_type: VISUALIZATION_TYPES.COLUMN_LINE,
      view_by_time_interval: 'week',
      date_range: dateRangeConfig,
      disable_legend: true,
      visualization_options: {
        hide_axes: true,
        chart_height: TREND_CHART_HEIGHT,
        chart_width: TREND_CHART_WIDTH,
      },
      chart_series: chartSeries,
    };
  }

  get primaryMetricChartDefinition() {
    return {
      id: `primary-metric-${this.topicName}-${this.subtopicName}`,
      view_by: null,
      visualization_type: VISUALIZATION_TYPES.COUNTER,
      view_by_time_interval: 'day',
      visualization_options: {
        show_time_comparison: true,
      },
      chart_series: [{ metric_id: this.args.trendingTopic.primaryMetric }],
    };
  }

  @action
  onShowConversationExplorer(topic: TrendingTopic) {
    this.args.onShowConversationExplorer(topic);
  }

  @action
  onCardClick() {
    this.onShowConversationExplorer(this.args.trendingTopic);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Automation::FinAiAgent::Analyze::TopicTrends::TrendingTopicCard': typeof TrendingTopicCard;
  }
}
